import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Admin-scoped token key. Kept fully isolated from the customer portal which
// uses `velcor_portal_token`. We migrate the legacy `velcor_token` once on
// boot so existing logged-in admins stay logged in after the rename.
const ADMIN_TOKEN_KEY = "velcor_admin_token";
try {
  const legacy = localStorage.getItem("velcor_token");
  if (legacy && !localStorage.getItem(ADMIN_TOKEN_KEY)) {
    localStorage.setItem(ADMIN_TOKEN_KEY, legacy);
  }
  // Always drop the legacy key so it can never be used by another surface.
  if (legacy) localStorage.removeItem("velcor_token");
  // Drop stale admin user blob that no code writes anymore.
  localStorage.removeItem("velcor_admin");
} catch (_) { /* storage unavailable */ }

export const ADMIN_TOKEN_STORAGE_KEY = ADMIN_TOKEN_KEY;

export const api = axios.create({
  baseURL: `${BACKEND}/api`,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Response interceptor: on 401 (admin token expired/invalid),
// clear creds and redirect to /admin/login instead of throwing
// an uncaught runtime error overlay across the whole app.
//
// IMPORTANT: 403 is NOT treated as a session-expired signal — agents legitimately
// get 403 when they try to access super-admin-only endpoints (reveal password,
// payment methods, etc.). Those errors must bubble up to the calling component
// so it can show a proper "permission denied" toast.
//
// We ALSO scope the 401 redirect to admin routes only, so a 401 from a
// background admin call while the user is browsing the public site or the
// customer portal does not yank them out of context.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      const onAdminRoute =
        typeof window !== "undefined" &&
        window.location.pathname.startsWith("/admin") &&
        !window.location.pathname.startsWith("/admin/login");
      if (onAdminRoute) {
        try {
          localStorage.removeItem(ADMIN_TOKEN_KEY);
        } catch (_) {
          /* ignore */
        }
        const next = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        window.location.replace(`/admin/login?expired=1&next=${next}`);
        // Swallow the error so the component does not show a runtime overlay
        return new Promise(() => {});
      }
    }
    return Promise.reject(error);
  },
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
